import { useState, useEffect } from 'react';
import { get } from '../../api';
import { CurrencyDollarIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';

export default function RevenueProjectionsPage() {
  const [projectionData, setProjectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjections = async () => {
      try {
        const data = await get('/api/1/admin/revenue-projections');
        setProjectionData(data);
        // If there's an error message but status is 200, treat it as an error state
        if (data.error) {
          setError(data.error);
        } else {
          setError(null);
        }
      } catch (err) {
        setError('Failed to load revenue projections');
        console.error('Error fetching projections:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjections();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-foreground mb-6">Revenue Projections</h1>
        <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <ChartBarIcon className="h-6 w-6 text-accent mr-2" />
            <h2 className="text-xl font-semibold text-foreground">No Data Available</h2>
          </div>
          <div className="bg-danger-soft border border-danger-border rounded-md p-4">
            <p className="text-danger">{error}</p>
            <p className="text-danger/80 mt-2 text-sm">Revenue projections will be available once users start making purchases.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!projectionData || !projectionData.projections || projectionData.projections.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-foreground mb-6">Revenue Projections</h1>
        <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-4">
            <ChartBarIcon className="h-6 w-6 text-accent mr-2" />
            <h2 className="text-xl font-semibold text-foreground">No Data Available</h2>
          </div>
          <p className="text-muted-foreground">No revenue projections available. Check back once users start making purchases.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-foreground">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Revenue Projections</h1>

      {/* Summary Card */}
      <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-accent mr-2" />
          <h2 className="text-xl font-semibold text-foreground">30-Day Projection Summary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Projected Revenue</span>
              <CurrencyDollarIcon className="h-5 w-5 text-success" />
            </div>
            <div className="text-2xl font-bold text-success mt-2">
              ${projectionData.total_projected_revenue_30d.toFixed(2)}
            </div>
          </div>
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Active Paying Users</span>
              <svg className="h-5 w-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="text-2xl font-bold text-accent mt-2">
              {projectionData.projections.length}
            </div>
          </div>
          <div className="bg-background-soft border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Avg. Weekly Usage</span>
              <ClockIcon className="h-5 w-5 text-info" />
            </div>
            <div className="text-2xl font-bold text-info mt-2">
              {(projectionData.projections.reduce((acc, curr) => acc + curr.weekly_usage, 0) / projectionData.projections.length).toFixed(1)} credits
            </div>
          </div>
        </div>
      </div>

      {/* User Projections Table */}
      <div className="bg-background-elevated border border-border rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-4">
          <svg className="h-6 w-6 text-accent mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h2 className="text-xl font-semibold text-foreground">User-Level Projections</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Current Credits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Weekly Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Weeks Until Purchase</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Projected Purchase</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {projectionData.projections.map((projection, index) => (
                <tr key={projection.user_id} className={index % 2 === 0 ? 'bg-background-elevated' : 'bg-background-soft'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{projection.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.current_credits}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{projection.weekly_usage}/week</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                      projection.weeks_until_purchase <= 1 ? 'bg-danger-soft text-danger-soft-foreground border-danger-border' :
                      projection.weeks_until_purchase <= 4 ? 'bg-warning-soft text-warning-soft-foreground border-warning-border' :
                      'bg-success-soft text-success-soft-foreground border-success-border'
                    }`}>
                      {projection.weeks_until_purchase === Infinity ? '∞' : `${projection.weeks_until_purchase} weeks`}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">${projection.projected_purchase.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}